/**
 * Scheduled prompts and their delivered answers (SellerMate W6.3) — the AI
 * side panel's Briefings tab.
 *
 * Top: the answers the scheduler delivered (daily KPI briefing, weekly waste
 * scan, anything the user scheduled), newest first; unread ones are marked
 * and opening one marks it read. Below: the schedules themselves — switch on
 * or off, run now, delete, or add one. Everything here is read-only against
 * ads data; the agent that answers cannot change anything.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Loader2, Play, Plus, Trash2 } from 'lucide-react'
import { Badge, Button, IconButton, Input, MicroLabel, Select, Skeleton } from '../ui'
import { ErrorState } from '../ErrorState'
import { useApiQuery } from '../../lib/query/useApiQuery'
import { invalidatePrefix } from '../../lib/query/invalidate'
import { formatRelative } from '../../lib/format'
import {
  aiApi, aiKeys, type Briefing, type Cadence, type ScheduledPrompt, type ScheduleInput,
} from '../../lib/aiApi'
import { hourLabel, scheduleLabel, WEEKDAYS } from '../../lib/promptLibrary'
import { cn } from '../../lib/cn'

export type ScheduleDraft = { name: string; prompt: string }

export default function ScheduledBriefings({
  draft, onDraftConsumed,
}: {
  /** A library prompt the user chose to schedule — opens the add form. */
  draft?: ScheduleDraft | null
  onDraftConsumed?: () => void
}) {
  const client = useQueryClient()
  const briefings = useApiQuery(aiKeys.briefings(), aiApi.briefings, { poll: 5 * 60_000 })
  const schedules = useApiQuery(aiKeys.schedules(), aiApi.schedules)
  const [openId, setOpenId] = useState<number | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<unknown>(null)

  const refresh = () => invalidatePrefix(client, ['ai'])

  async function act(id: number, fn: () => Promise<unknown>) {
    setBusyId(id)
    setActionError(null)
    try {
      await fn()
      await refresh()
    } catch (e) {
      setActionError(e)
    } finally {
      setBusyId(null)
    }
  }

  function toggleOpen(b: Briefing) {
    const next = openId === b.id ? null : b.id
    setOpenId(next)
    if (next !== null && !b.read_at) void act(b.id, () => aiApi.markRead(b.id))
  }

  const items = briefings.data?.briefings ?? []
  const unread = briefings.data?.unread ?? 0

  return (
    <div className="flex flex-col gap-4">
      {actionError != null && <ErrorState error={actionError} compact />}

      <section aria-label="Delivered briefings">
        <div className="flex items-center justify-between mb-1.5">
          <MicroLabel>Delivered{unread > 0 ? ` · ${unread} new` : ''}</MicroLabel>
          {unread > 0 && (
            <Button size="xs" variant="ghost" onClick={() => void act(-1, aiApi.markAllRead)}>
              Mark all read
            </Button>
          )}
        </div>
        {briefings.error && <ErrorState error={briefings.error} onRetry={() => void briefings.refetch()} compact />}
        {briefings.isLoading && <Skeleton className="h-16" />}
        {!briefings.isLoading && !briefings.error && items.length === 0 && (
          <p className="text-sm text-ink-muted">
            Nothing delivered yet. Scheduled prompts land here after their first run.
          </p>
        )}
        <ul className="space-y-1.5">
          {items.map((b) => {
            const open = openId === b.id
            return (
              <li key={b.id} className="rounded-lg border border-edge bg-surface">
                <button
                  type="button"
                  onClick={() => toggleOpen(b)}
                  aria-expanded={open}
                  className="w-full flex items-center gap-2 px-2.5 py-2 text-left"
                >
                  {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
                  <span className={cn('text-sm flex-1 min-w-0 truncate', b.read_at ? 'text-ink-secondary' : 'text-ink font-semibold')}>
                    {b.schedule_name}
                  </span>
                  {!b.read_at && <Badge tone="accent">New</Badge>}
                  {b.status === 'error' && <Badge tone="danger">Failed</Badge>}
                  <span className="text-xs text-ink-faint shrink-0">{formatRelative(b.started_at)}</span>
                </button>
                {open && (
                  <div className="px-3 pb-3 text-sm whitespace-pre-wrap text-ink">
                    {b.status === 'ok' ? b.answer : (
                      <span className="text-danger">This run did not finish: {b.error}</span>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      <section aria-label="Scheduled prompts">
        <MicroLabel className="mb-1.5">Schedules</MicroLabel>
        {schedules.error && <ErrorState error={schedules.error} onRetry={() => void schedules.refetch()} compact />}
        {schedules.isLoading && <Skeleton className="h-16" />}
        <ul className="space-y-1.5">
          {(schedules.data?.schedules ?? []).map((s) => (
            <ScheduleRow
              key={s.id}
              s={s}
              busy={busyId === s.id}
              onToggle={() => void act(s.id, () => aiApi.updateSchedule(s.id, { enabled: !s.enabled }))}
              onRun={() => void act(s.id, () => aiApi.runSchedule(s.id))}
              onDelete={() => void act(s.id, () => aiApi.deleteSchedule(s.id))}
            />
          ))}
        </ul>
        <AddSchedule
          key={draft ? `${draft.name}:${draft.prompt}` : 'blank'}
          draft={draft ?? null}
          onDone={() => { onDraftConsumed?.(); void refresh() }}
        />
      </section>
    </div>
  )
}

function ScheduleRow({
  s, busy, onToggle, onRun, onDelete,
}: {
  s: ScheduledPrompt; busy: boolean
  onToggle: () => void; onRun: () => void; onDelete: () => void
}) {
  return (
    <li className="rounded-lg border border-edge bg-surface p-2.5">
      <div className="flex items-start gap-2">
        <label className="mt-0.5 inline-flex items-center">
          <input
            type="checkbox"
            checked={s.enabled}
            onChange={onToggle}
            disabled={busy}
            aria-label={s.enabled ? `Turn off ${s.name}` : `Turn on ${s.name}`}
            className="accent-accent"
          />
        </label>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ink truncate">{s.name}</div>
          <div className="text-xs text-ink-muted">
            {scheduleLabel(s)}
            {s.enabled && s.next_run_at ? ` · next ${formatRelative(s.next_run_at)}` : ''}
            {!s.enabled ? ' · off' : ''}
          </div>
        </div>
        {busy ? <Loader2 size={14} className="animate-spin text-ink-faint mt-1" aria-label="Working" /> : (
          <>
            <IconButton aria-label={`Run ${s.name} now`} title="Run now" onClick={onRun}>
              <Play size={13} />
            </IconButton>
            <IconButton aria-label={`Delete ${s.name}`} title="Delete" onClick={onDelete}>
              <Trash2 size={13} />
            </IconButton>
          </>
        )}
      </div>
    </li>
  )
}

function AddSchedule({ draft, onDone }: { draft: ScheduleDraft | null; onDone: () => void }) {
  const [open, setOpen] = useState(draft !== null)
  const [name, setName] = useState(draft?.name ?? '')
  const [prompt, setPrompt] = useState(draft?.prompt ?? '')
  const [cadence, setCadence] = useState<Cadence>('daily')
  const [weekday, setWeekday] = useState(0)
  const [hour, setHour] = useState(7)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)

  if (!open) {
    return (
      <Button size="sm" variant="ghost" className="mt-2" onClick={() => setOpen(true)}>
        <Plus size={13} aria-hidden /> Add a schedule
      </Button>
    )
  }

  async function save() {
    const body: ScheduleInput = {
      name: name.trim(), prompt: prompt.trim(), cadence,
      weekday: cadence === 'weekly' ? weekday : null, hour_et: hour,
    }
    setSaving(true)
    setError(null)
    try {
      await aiApi.createSchedule(body)
      setOpen(false)
      onDone()
    } catch (e) {
      setError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-2 rounded-lg border border-edge bg-surface-2 p-2.5 space-y-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" aria-label="Schedule name" className="w-full" />
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="What should the assistant answer each time?"
        aria-label="Prompt"
        rows={3}
        className="w-full rounded-lg border border-edge-strong bg-surface text-ink text-sm px-3 py-2 placeholder:text-ink-faint focus:outline-none focus:border-accent"
      />
      <div className="flex flex-wrap gap-2">
        <Select value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)} aria-label="How often">
          <option value="daily">Every day</option>
          <option value="weekly">Every week</option>
        </Select>
        {cadence === 'weekly' && (
          <Select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} aria-label="Weekday">
            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </Select>
        )}
        <Select value={hour} onChange={(e) => setHour(Number(e.target.value))} aria-label="Hour (Eastern)">
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)} ET</option>)}
        </Select>
      </div>
      {error != null && <ErrorState error={error} compact />}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        <Button size="sm" variant="primary" disabled={saving || !name.trim() || !prompt.trim()} onClick={() => void save()}>
          {saving && <Loader2 size={13} className="animate-spin" aria-hidden />} Save schedule
        </Button>
      </div>
    </div>
  )
}
